<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Search Form
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="loadReport">
                  <v-row class="px-2 pt-4 pb-0">
                    <v-col lg="3" md="3" cols="12">
                      <v-autocomplete
                        label="Style"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        v-model="style_id.value.value"
                        :items="styleList"
                        :item-title="item => item.code || item.name"
                        :item-value="item => item.id"
                        :error-messages="style_id.errorMessage.value"
                        autocomplete="off"
                        clearable
                      ></v-autocomplete>
                    </v-col>

                    <v-col lg="3" md="3" cols="12">
                      <v-autocomplete
                        label="Buyer"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        v-model="buyer_id.value.value"
                        :items="buyerList"
                        :item-title="item => item.name"
                        :item-value="item => item.id"
                        :error-messages="buyer_id.errorMessage.value"
                        autocomplete="off"
                        clearable
                      ></v-autocomplete>
                    </v-col>

                    <v-col lg="2" md="2" cols="12">
                      <v-btn
                        class="bg-brand w-15"
                        type="submit"
                        ref="searchButton"
                        :loading="loading"
                      >
                        Search
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- Report Table -->
      <v-container fluid style="margin-top: -20px">
        <v-row>
          <v-col cols="12">
            <v-expansion-panels v-model="reportPanel">
              <v-expansion-panel>
                <v-expansion-panel-title
                  color="blue-grey-lighten-4"
                  class="panel-header"
                  expand-icon="mdi-chevron-down"
                >
                  <v-icon class="me-2">mdi-table</v-icon>
                  Style Report Data
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-row class="py-2">
                    <v-col md="4" cols="12">
                      <v-text-field
                        v-model="search"
                        append-inner-icon="mdi-magnify"
                        label="Search"
                        single-line
                        hide-details
                        density="compact"
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        class="pl-2"
                        clearable
                      ></v-text-field>
                    </v-col>

                    <v-col md="5" cols="12">
                      <div class="d-flex align-center flex-column">
                        <v-btn-group
                          variant="outlined"
                          density="compact"
                          size="x-small"
                          divided
                        >
                          <v-btn
                            prepend-icon="mdi-download"
                            variant="flat"
                            color="success"
                            @click="exportToCSV"
                          >
                            CSV
                          </v-btn>
                          <v-btn
                            prepend-icon="mdi-download"
                            variant="flat"
                            color="primary"
                            @click="exportToExcel"
                          >
                            EXCEL
                          </v-btn>
                          <v-btn
                            prepend-icon="mdi-download"
                            variant="flat"
                            color="red"
                            @click="exportToPDF"
                          >
                            PDF
                          </v-btn>
                        </v-btn-group>
                      </div>
                    </v-col>
                  </v-row>

                  <v-row class="pt-0">
                    <v-col cols="12">
                      <div class="scrollable-table-wrapper">
                        <v-data-table
                          :headers="headers"
                          :items="reportData"
                          :loading="loading"
                          v-model:items-per-page="itemsPerPage"
                          :search="search"
                          :hover="true"
                          density="compact"
                          class="responsive-table"
                        >
                          <template v-slot:item.index="{ index }">
                            {{ ++index }}
                          </template>

                          <template v-slot:item.code="{ item }">
                            {{ item.code || "-" }}
                          </template>

                          <template v-slot:item.buyer="{ item }">
                            {{ item.buyer?.name || "-" }}
                          </template>

                          <template v-slot:item.pos_count="{ item }">
                            {{ item.pos?.length || 0 }}
                          </template>

                          <template v-slot:item.boms_count="{ item }">
                            {{ item.boms?.length || 0 }}
                          </template>

                          <template v-slot:item.status="{ item }">
                            <v-chip
                              size="small"
                              :color="item.status == 1 ? 'success' : 'red'"
                            >
                              {{ item.status == 1 ? "Active" : "Inactive" }}
                            </v-chip>
                          </template>

                          <template v-slot:no-data>
                            <div class="text-center py-4">
                              <p>No style data found</p>
                            </div>
                          </template>
                        </v-data-table>
                      </div>
                    </v-col>
                  </v-row>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>
      </v-container>
    </v-container>
  </v-app>
</template>

<script>
import { ref } from "vue";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import Axios from "axios";
import { prefix } from "@/store/api";

export default {
  name: "StyleReport",
  
  data() {
    return {
      panel: [0],
      reportPanel: [0],
      loading: false,
      search: "",
      itemsPerPage: 10,
      reportData: [],
      headers: [
        {
          title: "#SL",
          align: "center",
          key: "index",
          width: "80px",
          sortable: false,
        },
        {
          title: "Code",
          key: "code",
          align: "left",
          sortable: true,
        },
        {
          title: "Buyer",
          key: "buyer",
          align: "left",
          sortable: false,
        },
        {
          title: "PO Count",
          key: "pos_count",
          align: "center",
          sortable: false,
        },
        {
          title: "BOM Count",
          key: "boms_count",
          align: "center",
          sortable: false,
        },
        {
          title: "Status",
          key: "status",
          align: "center",
          sortable: true,
        },
      ],
    };
  },

  setup() {
    const store = useStore();

    const { handleSubmit } = useForm({
      validationSchema: yup.object({
        style_id: yup.number().nullable(),
        buyer_id: yup.number().nullable(),
      }),
    });

    const style_id = useField("style_id");
    const buyer_id = useField("buyer_id");

    return {
      style_id,
      buyer_id,
    };
  },

  computed: {
    styleList() {
      return this.$store.getters.getProductionStyleList || [];
    },
    buyerList() {
      return this.$store.getters.getCustomerList || [];
    },
  },

  methods: {
    loadReport() {
      this.loading = true;
      const params = {};
      
      if (this.style_id.value.value) {
        params.style_id = this.style_id.value.value;
      }
      
      if (this.buyer_id.value.value) {
        params.buyer_id = this.buyer_id.value.value;
      }

      Axios.get(`${prefix}production/reports/styles`, { params })
        .then((response) => {
          this.reportData = response.data.data || [];
          if (this.reportData.length > 0) {
            toast.success("Report loaded successfully!");
          } else {
            toast.info("No data found for the selected criteria");
          }
        })
        .catch((err) => {
          toast.error(err.response?.data?.message || "Failed to load report");
          this.reportData = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    exportToCSV() {
      toast.info("CSV export functionality to be implemented");
    },
    exportToExcel() {
      toast.info("Excel export functionality to be implemented");
    },
    exportToPDF() {
      toast.info("PDF export functionality to be implemented");
    },
  },

  created() {
    // Load required data
    if (this.$store.getters.getProductionStyleList.length === 0) {
      this.$store.dispatch("getProductionStyleList");
    }
    if (this.$store.getters.getCustomerList.length === 0) {
      this.$store.dispatch("getCustomerList", {});
    }
  },
};
</script>

<style scoped>
.scrollable-table-wrapper {
  overflow-x: auto;
}

.responsive-table {
  min-width: 100%;
}
</style>

